import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Textarea } from '@/components/ui/textarea';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';

/**
 * MentionInput - Input com autocomplete para @menções
 * 
 * Props:
 * - value: string - Valor atual do input
 * - onChange: (value: string) => void - Callback quando valor muda
 * - placeholder: string - Placeholder do input
 * - className: string - Classes CSS adicionais
 * - rows: number - Número de linhas do textarea
 * - maxLength: number - Tamanho máximo do texto
 * - disabled: boolean - Se o input está desabilitado
 */
const MentionInput = ({ 
  value, 
  onChange, 
  placeholder = 'Digite sua mensagem...', 
  className = '',
  rows = 3,
  maxLength = 1000,
  disabled = false,
  onSubmit
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartPos, setMentionStartPos] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Buscar usuários para autocomplete
  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/users/search?q=${encodeURIComponent(query)}&limit=5`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestions(response.data.users || []);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Debounce na busca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mentionQuery) {
        searchUsers(mentionQuery);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [mentionQuery, searchUsers]);
  
  // Detectar @ no input
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    onChange(newValue);
    
    // Verificar se está digitando uma menção
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (mentionMatch) {
      setMentionStartPos(cursorPos - mentionMatch[0].length);
      setMentionQuery(mentionMatch[1]);
      setShowSuggestions(true);
      setSelectedIndex(0);
    } else {
      setShowSuggestions(false);
      setMentionQuery('');
      setMentionStartPos(-1);
    }
  };
  
  // Selecionar usuário do autocomplete
  const selectUser = (user) => {
    if (mentionStartPos === -1) return;
    
    const beforeMention = value.substring(0, mentionStartPos);
    const afterMention = value.substring(mentionStartPos + mentionQuery.length + 1);
    const username = user.username || user.name.toLowerCase().replace(/\s+/g, '');
    
    const newValue = `${beforeMention}@${username} ${afterMention}`;
    onChange(newValue);
    
    setShowSuggestions(false);
    setMentionQuery('');
    setMentionStartPos(-1);
    setSuggestions([]);
    
    // Focar no input
    if (inputRef.current) {
      const newCursorPos = beforeMention.length + username.length + 2;
      inputRef.current.focus();
      setTimeout(() => {
        inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };
  
  // Navegação por teclado
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) {
      // Submit com Enter (sem Shift)
      if (e.key === 'Enter' && !e.shiftKey && onSubmit) {
        e.preventDefault();
        onSubmit();
      }
      return;
    }
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (suggestions[selectedIndex]) {
          selectUser(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };
  
  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative">
      <Textarea
        ref={inputRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={`bg-[#0D0D0D] border-[#2A2A2A] text-white resize-none ${className}`}
        rows={rows}
        maxLength={maxLength}
        disabled={disabled}
      />
      
      {/* Autocomplete Dropdown */}
      {showSuggestions && (suggestions.length > 0 || loading) && (
        <div 
          ref={suggestionsRef}
          className="absolute bottom-full left-0 right-0 mb-1 bg-[#1A1A1A] border border-[#C79F5E]/30 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto"
        >
          {loading ? (
            <div className="p-3 text-center text-gray-400 text-sm">
              <span className="animate-spin inline-block mr-2">⏳</span>
              Buscando...
            </div>
          ) : (
            suggestions.map((user, index) => (
              <div
                key={user.id}
                onClick={() => selectUser(user)}
                className={`flex items-center gap-3 p-2 cursor-pointer transition-colors ${
                  index === selectedIndex 
                    ? 'bg-[#4D1A4A]/50' 
                    : 'hover:bg-[#2A2A2A]'
                }`}
              >
                <img
                  src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=C79F5E&color=000`}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {user.name}
                    {user.verified && <span className="text-[#C79F5E] ml-1">✓</span>}
                  </p>
                  <p className="text-gray-400 text-xs truncate">
                    @{user.username || user.name.toLowerCase().replace(/\s+/g, '')}
                  </p>
                </div>
                {user.role === 'creator' && (
                  <span className="text-xs bg-[#4D1A4A]/50 text-[#C79F5E] px-1.5 py-0.5 rounded">
                    Criador
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      )}
      
      {/* Dica de menção */}
      <div className="text-xs text-gray-500 mt-1">
        Digite @ para mencionar alguém
      </div>
    </div>
  );
};

export default MentionInput;
